import { readonly, ref } from 'vue'

import { maLogger } from '@/base/logger'

const state = {
  isLoaded: ref(false),
  isReady: ref(false),
}

// Default HubSpot configuration
const defaultConfig = {
  loadImmediately: true,
}

let portalId = null
let config = { ...defaultConfig }
const SCRIPT_ID = 'ma-hubspot-chat-widget'

/** @private */
function onConversationsAPIReady() {
  state.isReady.value = true
}

function loadScript() {
  return new Promise((resolve, reject) => {
    // Check if script is already loaded or loading
    if (
      document.getElementById(SCRIPT_ID)
      || window.HubSpotConversations
      || Array.from(document.getElementsByTagName('script')).some(
        s => s.src && s.src.includes(`js.hs-scripts.com/${portalId}.js`),
      )
    ) {
      state.isLoaded.value = true
      resolve()
      return
    }

    window.hsConversationsSettings = {
      ...config,
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `//js.hs-scripts.com/${portalId}.js`
    script.async = true
    script.defer = true

    script.onload = () => {
      state.isLoaded.value = true
      resolve()
    }

    script.onerror = (error) => {
      maLogger.error('Failed to load HubSpot chat:', error)
      reject(error)
    }

    document.head.appendChild(script)

    window.hsConversationsOnReady = [onConversationsAPIReady]
  })
}

const hubspotService = {
  isLoaded: readonly(state.isLoaded),
  isReady: readonly(state.isReady),

  async init() {
    if (!state.isLoaded.value) {
      try {
        await loadScript()
        maLogger.log('HubSpot Chat successfully loaded')
        return true
      }
      catch (error) {
        maLogger.error('Error loading HubSpot Chat:', error)
        return false
      }
    }
    return Promise.resolve(true)
  },

  identify(email, properties = {}) {
    if (!state.isLoaded.value) {
      maLogger.warn('HubSpot not loaded yet, unable to identify user')
      return
    }

    window.hsConversationsSettings = {
      ...window.hsConversationsSettings,
      identificationEmail: email,
      contactProperties: properties,
    }

    if (window.HubSpotConversations) {
      window.HubSpotConversations.widget.refresh()
    }
  },

  clearIdentity() {
    if (!state.isLoaded.value) {
      maLogger.warn('HubSpot not loaded yet, unable to clear user identity')
      return
    }

    if (window.HubSpotConversations) {
      window.HubSpotConversations.clear({ resetWidget: true })
    }
  },

  open() {
    if (window.HubSpotConversations) {
      window.HubSpotConversations.widget.open()
    }
  },

  close() {
    if (window.HubSpotConversations) {
      window.HubSpotConversations.widget.close()
    }
  },

  hide() {
    if (window.HubSpotConversations) {
      window.HubSpotConversations.widget.remove()
    }
  },

  show() {
    if (window.HubSpotConversations) {
      window.HubSpotConversations.widget.load()
    }
  },

  refresh() {
    if (window.HubSpotConversations) {
      window.HubSpotConversations.widget.refresh()
    }
  },
}

/** Global access function */
export function useHubspot() {
  return hubspotService
}

/** Vue plugin */
export default {
  install(app, options = {}) {
    portalId = options.portalId

    if (!portalId) {
      maLogger.error('HubSpot plugin requires a portalId')
      return
    }

    // Update default config with user options
    if (options.config) {
      config = {
        ...config,
        ...options.config,
      }
    }

    // Configure router if provided
    if (options.router) {
      options.router.afterEach(() => {
        // Refresh chat on route changes with slight delay to ensure DOM is updated
        setTimeout(() => {
          if (window.HubSpotConversations && state.isLoaded.value) {
            hubspotService.refresh()
          }
        }, 100)
      })
    }

    // Load HubSpot script if configured to load immediately
    if (config.loadImmediately) {
      hubspotService.init()
    }

    // Make hubspot available via app.config.globalProperties
    app.config.globalProperties.$hubspot = hubspotService
  },
}
